import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { routes } from '../../routes.js';
import { trpc } from '../../lib/trpc.js';
import { Button, FormField, TextInput, TextArea, Modal } from '../../components/ui/index.js';

/**
 * Public landing page at /. Deliberately one-screen-ish and quiet — Scope
 * is in private beta, and the page reflects that. No feature tours, no
 * pricing tables, no testimonials yet. As the product opens up this grows
 * into a real marketing surface.
 *
 * "Request an invite" opens a small form (trpc.marketing.requestInvite)
 * that emails the submission to whoever's running the beta inbox
 * (INVITE_REQUEST_RECIPIENT, default anton@gestureloop.com).
 */
export function Landing() {
  const [inviteOpen, setInviteOpen] = useState(false);
  return (
    <div className="flex min-h-screen flex-col bg-canvas text-scope-navy">
      {/* Header */}
      <header className="mx-auto flex w-full max-w-content items-center justify-between px-6 py-6 md:px-10">
        <img
          src="/icons/scope-wordmark.svg"
          alt="Scope"
          className="h-7 w-auto md:h-8"
          width="129"
          height="40"
        />
        <Link
          to={routes.login}
          className="text-sm font-medium text-scope-text-muted transition hover:text-scope-navy"
        >
          Sign in
        </Link>
      </header>

      {/* Hero */}
      <main className="mx-auto w-full max-w-content flex-1 px-6 pt-10 pb-16 md:px-10 md:pt-20 md:pb-24">
        <section className="max-w-3xl">
          <h1 className="text-4xl font-semibold tracking-tight md:text-5xl">
            Keep every engagement in scope.
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-scope-text-muted md:text-xl">
            Scope is the workspace for professional services firms and agencies. Track every
            deliverable, give your clients a portal they trust, and stop running serious work on a
            patchwork of spreadsheets and Slack threads.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={() => setInviteOpen(true)}
              className="inline-flex items-center gap-2 rounded-lg bg-scope-navy px-5 py-2.5 text-sm font-medium text-white transition hover:bg-scope-navy-soft focus:outline-none focus:ring-2 focus:ring-scope-green focus:ring-offset-2 focus:ring-offset-canvas"
            >
              Request an invite
            </button>
            <span className="text-sm text-scope-text-faint">Currently in private beta.</span>
          </div>
        </section>

        {/* Three quiet value pillars */}
        <section className="mt-20 grid gap-10 border-t border-scope-border-soft pt-12 md:mt-24 md:grid-cols-3 md:gap-12 md:pt-16">
          <Pillar
            title="For your team"
            body="Clients, engagements, projects, deliverables and recurring retainer work — in one place that doesn’t feel like a project-management tax."
          />
          <Pillar
            title="For your clients"
            body="A portal that shows them what you’ve delivered, what’s coming, and what needs their approval. Quietly proves the value of every invoice."
          />
          <Pillar
            title="Built deliberately"
            body="Calm tools. Plain language. No dashboards you’ll never look at, no notifications you didn’t ask for. The product gets out of your way."
          />
        </section>
      </main>

      {/* Navy footer band — proper brand closure with the wordmark on dark */}
      <footer className="bg-scope-navy text-scope-cream">
        <div className="mx-auto flex w-full max-w-content flex-col items-start justify-between gap-6 px-6 py-10 md:flex-row md:items-center md:px-10 md:py-12">
          <div>
            <img
              src="/icons/scope-wordmark-dark.svg"
              alt="Scope"
              className="h-7 w-auto md:h-8"
              width="129"
              height="40"
            />
            <p className="mt-3 text-sm text-scope-cream/70">Keep every engagement in scope.</p>
          </div>
          <div className="flex flex-col items-start gap-2 text-xs text-scope-cream/60 md:items-end">
            <div className="flex items-center gap-4">
              <Link to={routes.privacy} className="transition hover:text-scope-cream">
                Privacy
              </Link>
              <Link to={routes.terms} className="transition hover:text-scope-cream">
                Terms
              </Link>
              <Link to={routes.login} className="transition hover:text-scope-cream">
                Sign in
              </Link>
            </div>
            <span>© {new Date().getFullYear()} Scope</span>
          </div>
        </div>
      </footer>

      <RequestInviteModal open={inviteOpen} onClose={() => setInviteOpen(false)} />
    </div>
  );
}

function Pillar({ title, body }: { title: string; body: string }) {
  return (
    <div>
      <img
        src="/icons/scope-mark-green.svg"
        alt=""
        aria-hidden="true"
        className="h-5 w-5"
        width="20"
        height="20"
      />
      <h2 className="mt-3 text-sm font-semibold tracking-tight">{title}</h2>
      <p className="mt-2 text-sm leading-relaxed text-scope-text-muted">{body}</p>
    </div>
  );
}

const inviteSchema = z.object({
  name: z.string().min(1, 'Your name is required').max(120),
  email: z.string().email('Enter a valid email').max(255),
  organisationName: z.string().min(1, 'Your organisation name is required').max(160),
  message: z.string().max(2000).optional(),
  /** Honeypot — must stay empty. Real users never see this field. */
  website: z.string().max(0).optional(),
});

type InviteFormValues = z.infer<typeof inviteSchema>;

function RequestInviteModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [submitted, setSubmitted] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<InviteFormValues>({
    resolver: zodResolver(inviteSchema),
    mode: 'onTouched',
    defaultValues: { name: '', email: '', organisationName: '', message: '', website: '' },
  });

  const requestM = trpc.marketing.requestInvite.useMutation({
    onSuccess: () => {
      setSubmitted(true);
      reset();
    },
  });

  function handleClose() {
    onClose();
    // Reset after the close animation so the form doesn't flash blank on
    // its way out.
    setTimeout(() => {
      setSubmitted(false);
      requestM.reset();
      reset();
    }, 200);
  }

  const onSubmit = (values: InviteFormValues) => {
    requestM.mutate({
      name: values.name.trim(),
      email: values.email.trim(),
      organisationName: values.organisationName.trim(),
      message: values.message?.trim() || undefined,
      website: values.website,
    });
  };

  return (
    <Modal open={open} onClose={handleClose} size="md">
      <div className="-mx-6 -mt-5 mb-5 border-b border-scope-border-soft px-6 pb-4 pt-5">
        <h3 className="text-lg font-semibold text-scope-navy">Request an invite</h3>
        <p className="mt-0.5 text-xs text-scope-text-muted">
          We&rsquo;ll be in touch within a day or two.
        </p>
      </div>

      {submitted ? (
        <div className="py-2">
          <p className="text-sm text-scope-navy">
            Thanks — we&rsquo;ve got your request and will email you back shortly.
          </p>
          <div className="mt-6 flex justify-end">
            <Button onClick={handleClose}>Close</Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <FormField label="Your name" htmlFor="invite-name" required error={errors.name?.message}>
            <TextInput id="invite-name" autoComplete="name" {...register('name')} />
          </FormField>
          <FormField
            label="Work email"
            htmlFor="invite-email"
            required
            error={errors.email?.message}
          >
            <TextInput id="invite-email" type="email" autoComplete="email" {...register('email')} />
          </FormField>
          <FormField
            label="Organisation name"
            htmlFor="invite-organisation"
            required
            error={errors.organisationName?.message}
          >
            <TextInput
              id="invite-organisation"
              autoComplete="organization"
              {...register('organisationName')}
            />
          </FormField>
          <FormField
            label="Anything you'd like us to know? (optional)"
            htmlFor="invite-message"
            error={errors.message?.message}
          >
            <TextArea id="invite-message" rows={3} {...register('message')} />
          </FormField>

          {/* Honeypot — hidden from real users, bots tend to fill it in.
              Submissions that contain a value here are silently dropped
              server-side. tabIndex + aria-hidden keep assistive tech out. */}
          <div className="hidden" aria-hidden="true">
            <label>
              Website (leave blank)
              <input type="text" tabIndex={-1} autoComplete="off" {...register('website')} />
            </label>
          </div>

          {requestM.error && <p className="text-sm text-status-red">{requestM.error.message}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" loading={requestM.isPending}>
              Send request
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
